/**
 * CloseoutModal — the guided "close it out on the spot" wizard (CT-025 reconcile).
 *
 * Many stuck items are actually DONE but never marked complete. This walks the closeout: it enforces
 * the SAME gate PostMilestoneModal's own handleConfirm enforces (every VISIBLE checklist item must be
 * complete or marked not required), checks type-specific requirements, then hands off to the existing
 * post flow (open-post-ms-modal) which already encodes the standard / permit / revision variants +
 * their required inputs — so we reuse, not reinvent.
 *
 * Opened via `open-closeout-modal` ({ msId, name, projectId }); mounted once in OutlineView.
 *
 * PLACEHOLDER: the per-milestone-type requirements (e.g. drawing sheets attached for Permit Plan Check
 * Drawings) are stubbed in REQUIREMENTS — we fill them in one milestone type at a time. The checklist
 * gate is live now.
 *
 * QA/QC-category items are excluded from this gate (2026-09-04, Flight Board landing/fast-track ran
 * into this) — `HIDDEN_CL_CATEGORIES` (outline/constants.js) hides them from the outline entirely
 * (migrating to AlderQC), which left them permanently un-completable through any visible UI while this
 * modal's OWN gate — unlike PostMilestoneModal's — counted them anyway, so a milestone with QA/QC
 * checklist items could never close out here at all. This does NOT touch the separate drawing-sheet
 * QC-approval gate (qc_versions, enforced inside PostMilestoneModal itself once "Complete & post" hands
 * off to it) — that one stays exactly as strict as it already is; only the redundant pre-check here is
 * being brought in line with the real posting gate.
 */

import { useEffect, useState } from 'react'
import { getMilestoneChecklistItems } from '../services/checklist.js'
import { useScrollLock } from '../hooks/useScrollLock.js'
import { HIDDEN_CL_CATEGORIES } from '../outline/constants.js'

const getFirst = v => (Array.isArray(v) ? v[0] : v)

// Per-milestone-type extra closeout requirements. Keyed by `Name (from Deliverable)`. EMPTY for now —
// each entry returns a list of { ok, label } checks. Add one type at a time (drawing sheets, etc.).
const REQUIREMENTS = {
  // 'Permit Plan Check Drawings': (msId) => [ ...drawing-sheet checks... ],
}

export default function CloseoutModal() {
  const [target, setTarget] = useState(null)   // { msId, name, projectId } | null
  const [items, setItems] = useState(null)     // checklist items | null while loading

  // Reference-counted (hooks/useScrollLock.js) — this can now open ON TOP of a workspace
  // (Pulse's Flight Tracker, dragging a flight to the far right) that already holds its own lock.
  useScrollLock(!!target)

  useEffect(() => {
    function onOpen(e) {
      const d = e.detail || {}
      if (!d.msId) return
      setTarget({ msId: d.msId, name: d.name || 'Milestone', projectId: d.projectId })
      setItems(null)
      getMilestoneChecklistItems(d.msId).then(setItems).catch(() => setItems([]))
    }
    window.addEventListener('open-closeout-modal', onOpen)
    return () => window.removeEventListener('open-closeout-modal', onOpen)
  }, [])

  if (!target) return null
  const loading = items === null
  const list = items || []
  // Same split as PostMilestoneModal#handleConfirm: gated = every item OUTSIDE a hidden category
  // (QA/QC); incomplete = whichever of those aren't done or explicitly skipped. Hidden-category
  // items never appear here at all, gated or not — there's no UI for anyone to act on them.
  const gated = list.filter(i => HIDDEN_CL_CATEGORIES.indexOf(getFirst((i.fields || {})['Category (from Deliverable Checklist Item Template)']) || 'Other') === -1)
  const incomplete = gated.filter(i => {
    const f = i.fields || {}
    return f['Complete'] !== true && f['Milestone Not Required'] !== true
  })
  const ready = !loading && incomplete.length === 0

  function close() { setTarget(null); setItems(null) }
  function complete() {
    // Reuse the existing post flow — it handles the standard / permit / revision variants + inputs.
    window.dispatchEvent(new CustomEvent('open-post-ms-modal', { detail: { msId: target.msId } }))
    close()
  }
  const itemName = (i) => { const f = i.fields || {}; return f['Name'] || f['Checklist Item'] || (Array.isArray(f['Checklist Item Template']) ? '' : '') || 'Checklist item' }

  return (
    <>
      <div style={S.backdrop} onClick={close} />
      <div style={S.modal} role="dialog" aria-label="Close out milestone">
        <div style={S.headRow}>
          <span style={S.kicker}>Close out</span>
          <button style={S.x} onClick={close} aria-label="Close">×</button>
        </div>
        <div style={S.title}>{target.name}</div>
        <div style={S.sub}>Often these are done and just never marked complete. Finish the closeout here.</div>

        <div style={S.section}>
          <div style={S.secHead}>1 · Checklist</div>
          {loading ? <div style={S.muted}>Loading checklist…</div>
            : incomplete.length === 0 ? <div style={S.ok}>✓ All checklist items complete{gated.length ? ` (${gated.length})` : ''}</div>
              : <>
                  <div style={S.warn}>{incomplete.length} of {gated.length} still open — all must be complete or marked not required to close out.</div>
                  <div style={S.checklist}>
                    {incomplete.slice(0, 8).map(i => <div key={i.id} style={S.li}>○ {itemName(i)}</div>)}
                    {incomplete.length > 8 && <div style={S.muted}>+{incomplete.length - 8} more</div>}
                  </div>
                  <div style={S.muted}>Complete these on the milestone, then reopen this to finish closing out. (Inline completion is coming.)</div>
                </>}
        </div>

        <div style={S.section}>
          <div style={S.secHead}>2 · Requirements</div>
          <div style={S.muted}>Type-specific requirements (e.g. drawing sheets for Permit Plan Check Drawings) are checked here — added per milestone type. Placeholder for now.</div>
        </div>

        <div style={S.btns}>
          <button style={ready ? S.primary : S.primaryOff} disabled={!ready} onClick={complete}>Complete &amp; post</button>
          <button style={S.ghost} onClick={close}>Cancel</button>
        </div>
      </div>
    </>
  )
}

const S = {
  backdrop: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 1400 },
  modal: { position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 'min(460px, 92vw)', maxHeight: '86vh', overflowY: 'auto', background: 'var(--aj-bg-elevated)', borderRadius: 14, zIndex: 1401, padding: '16px 18px 18px', boxShadow: '0 18px 60px rgba(0,0,0,0.28)', fontFamily: '"DM Sans",sans-serif', color: 'var(--aj-text)' },
  headRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  kicker: { fontFamily: '"Montserrat",sans-serif', fontSize: 10.5, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--aj-text-muted)' },
  x: { fontSize: 22, lineHeight: 1, color: 'var(--aj-text-muted)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 },
  title: { fontSize: 17, fontWeight: 600, marginTop: 4, lineHeight: 1.3 },
  sub: { fontSize: 12, color: 'var(--aj-text-muted)', marginTop: 4, lineHeight: 1.5 },
  section: { marginTop: 16, paddingTop: 14, borderTop: '0.5px solid var(--aj-border)' },
  secHead: { fontFamily: '"Montserrat",sans-serif', fontSize: 10.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--aj-text)', marginBottom: 8 },
  ok: { fontSize: 13, fontWeight: 600, color: '#1FB57A' },
  warn: { fontSize: 12.5, color: '#b8540a', marginBottom: 8 },
  checklist: { display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 8 },
  li: { fontSize: 12.5, color: 'var(--aj-text-mid)' },
  muted: { fontSize: 11.5, color: 'var(--aj-text-muted)', lineHeight: 1.5 },
  btns: { display: 'flex', gap: 8, marginTop: 18 },
  primary: { fontFamily: '"DM Sans",sans-serif', fontSize: 13, fontWeight: 600, color: '#fff', background: '#07272D', border: 'none', borderRadius: 8, padding: '9px 16px', cursor: 'pointer' },
  primaryOff: { fontFamily: '"DM Sans",sans-serif', fontSize: 13, fontWeight: 600, color: 'var(--aj-text-muted)', background: 'var(--aj-bg-muted)', border: 'none', borderRadius: 8, padding: '9px 16px', cursor: 'not-allowed' },
  ghost: { fontFamily: '"DM Sans",sans-serif', fontSize: 13, color: 'var(--aj-text-mid)', background: 'none', border: '0.5px solid var(--aj-border-mid)', borderRadius: 8, padding: '9px 14px', cursor: 'pointer' },
}
